import aiLogger from '../components/ai-logger';

export interface FilePreview {
    file: File;
    url: string;
}

export class ChatFileUploadService {
    public static readonly ALLOWED_EXTENSIONS = [
        'jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg', 'pdf', 'doc', 'docx', 'xls', 'xlsx',
        'ppt', 'pptx', 'txt', 'md', 'csv', 'json', 'xml', 'zip', 'rar', '7z', 'drawio', 'vsdx',
        'sql', 'log', 'yml', 'yaml', 'php', 'py', 'js', 'ts', 'css', 'html', 'scss', 'less',
    ];

    /**
     * Valida se um arquivo é permitido baseado na extensão.
     */
    public static isValidFile(file: File): boolean {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        if (file.type.startsWith('image/')) return true;
        return this.ALLOWED_EXTENSIONS.includes(ext);
    }

    /**
     * Processa um drop de arquivos.
     */
    public static getFilesFromDrop(event: DragEvent): File[] {
        const dataTransfer = event.dataTransfer;
        if (!dataTransfer) return [];

        const droppedFiles: File[] = [];
        if (dataTransfer.files && dataTransfer.files.length > 0) {
            for (let i = 0; i < dataTransfer.files.length; i++) {
                droppedFiles.push(dataTransfer.files[i]);
            }
        } else if (dataTransfer.items) {
            for (let i = 0; i < dataTransfer.items.length; i++) {
                const item = dataTransfer.items[i];
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) droppedFiles.push(file);
                }
            }
        }
        return droppedFiles.filter(f => this.isValidFile(f));
    }

    /**
     * Processa o evento de paste para extrair imagens.
     */
    public static async getFilesFromPaste(event: ClipboardEvent): Promise<File[]> {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return [];

        const imageFiles: File[] = [];
        for (let i = 0; i < clipboardData.items.length; i++) {
            const item = clipboardData.items[i];
            if (item.type.startsWith('image/')) {
                const blob = item.getAsFile();
                if (blob) {
                    const ext = item.type.split('/')[1] || 'png';
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                    const fileName = `imagem-colada-${timestamp}.${ext}`;
                    imageFiles.push(new File([blob], fileName, { type: item.type }));
                }
            }
        }

        if (imageFiles.length === 0) {
            const htmlContent = clipboardData.getData('text/html');
            if (htmlContent && htmlContent.includes('<img')) {
                return await this.extractImagesFromHtml(htmlContent);
            }
        }

        return imageFiles;
    }

    /**
     * Extrai imagens de um conteúdo HTML colado.
     */
    private static async extractImagesFromHtml(html: string): Promise<File[]> {
        const files: File[] = [];
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const imgs = doc.querySelectorAll('img');
            const promises: Promise<File | null>[] = [];

            for (let i = 0; i < imgs.length; i++) {
                const src = imgs[i].getAttribute('src');
                if (!src) continue;

                const width = parseInt(imgs[i].getAttribute('width') || '0', 10);
                const height = parseInt(imgs[i].getAttribute('height') || '0', 10);
                if ((width > 0 && width < 10) || (height > 0 && height < 10)) continue;

                promises.push(this.fetchImageAsFile(src, i));
            }

            const results = await Promise.allSettled(promises);
            for (const result of results) {
                if (result.status === 'fulfilled' && result.value) {
                    files.push(result.value);
                }
            }
        } catch (e) {
            aiLogger.warn('[ChatFileUploadService] Error extracting images from HTML:', e);
        }
        return files;
    }

    private static async fetchImageAsFile(src: string, index: number): Promise<File | null> {
        try {
            if (src.startsWith('data:')) {
                const response = await fetch(src);
                const blob = await response.blob();
                if (blob.size < 500) return null;
                const ext = blob.type.split('/')[1] || 'png';
                return new File([blob], `imagem-${index + 1}.${ext}`, { type: blob.type });
            }

            if (src.startsWith('http://') || src.startsWith('https://')) {
                const response = await fetch(src, { mode: 'cors', signal: AbortSignal.timeout(5000) });
                if (!response.ok) return null;
                const blob = await response.blob();
                if (!blob.type.startsWith('image/') || blob.size < 500) return null;

                const urlPath = new URL(src).pathname;
                const urlName = urlPath.split('/').pop() || '';
                const ext = blob.type.split('/')[1] || 'png';
                const fileName = urlName && urlName.includes('.') ? urlName : `imagem-${index + 1}.${ext}`;
                return new File([blob], fileName, { type: blob.type });
            }
        } catch (e) {
            aiLogger.debug(`[ChatFileUploadService] Fetch failed for: ${src.substring(0, 50)}...`, e);
        }
        return null;
    }
}
